import { z } from 'zod';
import { IsoDateTime, JsonObject, Sha256Hex, TenantId } from './common.ts';
import { DecimalString } from './decimal.ts';
import { ErrorCode, PlatformError } from './errors.ts';
import { canonicalJson, sha256Hex } from './hash.ts';

/** Provider-neutral reasoning effort (added by 23). Adapters translate it or reject it; never silently drop it. */
export const ReasoningEffort = z.enum(['off', 'low', 'medium', 'high']).meta({ id: 'ReasoningEffort' });
export type ReasoningEffort = z.infer<typeof ReasoningEffort>;

/**
 * Bounded per-call model settings of a profile version (added by 23). Absent = adapter default. `temperature` and
 * `reasoningEffort` are forwarded only when the adapter declares the capability (`AdapterCapabilities`); otherwise
 * activation rejects a version that sets them.
 */
export const ModelSettings = z
  .object({
    reasoningEffort: ReasoningEffort.optional(),
    temperature: z.number().min(0).max(2).optional(),
    maxOutputTokens: z.number().int().positive().max(1_000_000).optional(),
    /** Hard per-request deadline in ms. Fallback attempts share it; it is never reset. */
    deadlineMs: z.number().int().positive().max(3_600_000).optional(),
  })
  .strict()
  .meta({ id: 'ModelSettings' });
export type ModelSettings = z.infer<typeof ModelSettings>;

/** A named model routing profile (blueprint §9), e.g. `reasoning`, `extraction`, `embeddings`. */
export const ModelProfile = z
  .object({
    name: z.string().min(1),
    /** Adapter name as bound by the profile, e.g. `model-anthropic`, `model-openai-compat`, `model-mock`. */
    adapter: z.string().min(1),
    model: z.string().min(1),
    intendedTasks: z.array(z.string()).default([]),
    region: z.string().default('global'),
    dataPolicy: z.string().default('no-training'),
    latencyClass: z.enum(['interactive', 'standard', 'batch']).default('standard'),
    fallbackEligible: z.boolean().default(false),
    /** Profiles tried in order when this one fails and `fallbackEligible` is true. */
    fallbackProfiles: z.array(z.string()).default([]),
    /** Consequential profiles never fall back silently. */
    consequential: z.boolean().default(false),
    maxContextTokens: z.number().int().positive().default(200_000),
    /** Output dimension for embedding profiles. */
    embeddingDimensions: z.number().int().positive().optional(),
    /** Adapter-specific settings (base URL, deployment name …). Secrets are references, never values. */
    config: z.record(z.string(), z.unknown()).default({}),
    /** Added by 23: bounded per-call settings (legacy profiles omit them). */
    settings: ModelSettings.optional(),
    /** Added by 23: the managed profile version this projection shows (absent for static YAML profiles). */
    version: z.number().int().positive().optional(),
  })
  .meta({ id: 'ModelProfile' });
export type ModelProfile = z.infer<typeof ModelProfile>;
export type ModelProfileInput = z.input<typeof ModelProfile>;

// ---------------------------------------------------------------------------------------------
// 23 — model management (ADR-022). Falak owns approval, versions, binding, fallback and audit; providers supply
// inference and metadata only. Every schema below is additive; `ModelProfile` above stays the legacy projection.
// ---------------------------------------------------------------------------------------------

/** What a model is used for. A profile, a catalogue entry and a route each have exactly one operation. */
export const ModelOperation = z.enum(['chat', 'embeddings']).meta({ id: 'ModelOperation' });
export type ModelOperation = z.infer<typeof ModelOperation>;

/**
 * Operation of a legacy `ModelProfile`, which has no explicit field: `embeddings` when it declares
 * `embeddingDimensions` or the intended task `embeddings`, else `chat`.
 */
export function modelProfileOperation(
  profile: Pick<ModelProfile, 'embeddingDimensions' | 'intendedTasks'>,
): ModelOperation {
  return profile.embeddingDimensions !== undefined || profile.intendedTasks.includes('embeddings')
    ? 'embeddings'
    : 'chat';
}

/** Tri-state capability. `unknown` is never treated as `supported`. */
export const Capability = z.enum(['supported', 'unsupported', 'unknown']).meta({ id: 'Capability' });
export type Capability = z.infer<typeof Capability>;

/** Capability names a catalogue entry describes and a profile version may require. */
export const ModelCapabilityName = z
  .enum(['tools', 'structuredOutput', 'streaming', 'reasoningEffort', 'temperature'])
  .meta({ id: 'ModelCapabilityName' });
export type ModelCapabilityName = z.infer<typeof ModelCapabilityName>;

/** Tri-state capabilities of one model; missing keys default to `unknown`. */
export const ModelCapabilities = z
  .object({
    tools: Capability.default('unknown'),
    structuredOutput: Capability.default('unknown'),
    streaming: Capability.default('unknown'),
    reasoningEffort: Capability.default('unknown'),
    temperature: Capability.default('unknown'),
  })
  .meta({ id: 'ModelCapabilities' });
export type ModelCapabilities = z.infer<typeof ModelCapabilities>;
export type ModelCapabilitiesInput = z.input<typeof ModelCapabilities>;

/**
 * A sparse set of capability values (operator assertions, manual overrides, evidence). Every key is optional and
 * has NO default: `{}` means "nothing asserted". `ModelCapabilities.partial()` must not be used for this, because
 * zod keeps each field's `'unknown'` default under `.partial()`, so `{}` would parse as "everything asserted
 * unknown" and override provider facts.
 */
export const ModelCapabilityAssertions = z
  .object({
    tools: Capability.optional(),
    structuredOutput: Capability.optional(),
    streaming: Capability.optional(),
    reasoningEffort: Capability.optional(),
    temperature: Capability.optional(),
  })
  .meta({ id: 'ModelCapabilityAssertions' });
export type ModelCapabilityAssertions = z.infer<typeof ModelCapabilityAssertions>;

/** Where a capability value comes from. Operator assertions are kept apart from provider-reported facts. */
export const CapabilitySource = z
  .enum(['provider', 'operator', 'legacy', 'adapter'])
  .meta({ id: 'CapabilitySource' });
export type CapabilitySource = z.infer<typeof CapabilitySource>;

/** Falak-owned approval state of a catalogue entry. A discovered or manual entry starts `unapproved`. */
export const ApprovalState = z.enum(['unapproved', 'approved', 'revoked']).meta({ id: 'ModelApprovalState' });
export type ApprovalState = z.infer<typeof ApprovalState>;

/** How a catalogue entry got there. `legacy` = imported from static YAML by bootstrap. */
export const ModelEntrySource = z.enum(['discovered', 'manual', 'legacy']).meta({ id: 'ModelEntrySource' });
export type ModelEntrySource = z.infer<typeof ModelEntrySource>;

/** Listing state from the last complete catalogue snapshot. `unlisted` = omitted by a later complete refresh. */
export const ModelAvailability = z
  .enum(['available', 'unlisted', 'unknown'])
  .meta({ id: 'ModelAvailability' });
export type ModelAvailability = z.infer<typeof ModelAvailability>;

/**
 * Whether a model id names one exact model. `router` (e.g. OpenRouter `openrouter/auto`) and unbounded `alias` ids
 * cannot serve managed profiles; an `alias` needs operator-declared `aliasTargets`.
 */
export const ModelIdentity = z.enum(['exact', 'alias', 'router']).meta({ id: 'ModelIdentity' });
export type ModelIdentity = z.infer<typeof ModelIdentity>;

/** Unit a price is quoted in. OpenRouter reports `per_token`; display uses `per_million_tokens`. */
export const ModelPriceUnit = z.enum(['per_token', 'per_million_tokens']).meta({ id: 'ModelPriceUnit' });
export type ModelPriceUnit = z.infer<typeof ModelPriceUnit>;

/**
 * Price metadata (decimal strings, never floats). A missing component is unknown, not free; a missing `ModelPrice`
 * (null) means the whole price is unknown. Extra charge categories (images, web search …) are kept in `extra` as
 * metadata and are not part of token cost estimates. `request` is a per-request charge, independent of `unit`.
 */
export const ModelPrice = z
  .object({
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/, 'ISO 4217 currency code')
      .default('USD'),
    unit: ModelPriceUnit,
    input: DecimalString.optional(),
    output: DecimalString.optional(),
    reasoning: DecimalString.optional(),
    cacheRead: DecimalString.optional(),
    cacheWrite: DecimalString.optional(),
    request: DecimalString.optional(),
    extra: z.record(z.string().max(64), DecimalString).default({}),
    asOf: IsoDateTime,
    source: CapabilitySource,
  })
  .meta({ id: 'ModelPrice' });
export type ModelPrice = z.infer<typeof ModelPrice>;

/** Known limits of a model. Absent = unknown. */
export const ModelLimits = z
  .object({
    contextTokens: z.number().int().positive().optional(),
    maxOutputTokens: z.number().int().positive().optional(),
    /** Output dimensions of an embeddings model. */
    embeddingDimensions: z.number().int().positive().optional(),
  })
  .meta({ id: 'ModelLimits' });
export type ModelLimits = z.infer<typeof ModelLimits>;

/** Opaque provider model id, e.g. `anthropic/claude-sonnet-4.5`. May contain slashes; never used as a path segment. */
export const ModelId = z
  .string()
  .min(1)
  .max(256)
  .regex(/^[\x21-\x7e]+$/, 'model id must be printable ASCII without spaces');

/** Connection id: lowercase kebab case, e.g. `openrouter-direct`, `legacy-model-openrouter`. */
export const ModelConnectionId = z
  .string()
  .regex(/^[a-z][a-z0-9-]{0,62}$/, 'connection id must be lowercase kebab case')
  .meta({ id: 'ModelConnectionId' });
export type ModelConnectionId = z.infer<typeof ModelConnectionId>;

/** Stable catalogue entry id `me_<26 base32 chars>` (see `modelCatalogEntryId`). */
export const ModelCatalogEntryId = z
  .string()
  .regex(/^me_[a-z2-7]{26}$/, 'catalogue entry id is me_ + 26 base32 characters')
  .meta({ id: 'ModelCatalogEntryId' });
export type ModelCatalogEntryId = z.infer<typeof ModelCatalogEntryId>;

const BASE32 = 'abcdefghijklmnopqrstuvwxyz234567';

/**
 * Catalogue entry id for `(connectionId, modelId)`: `me_` + the first 26 characters of lowercase RFC 4648 base32 (no
 * padding) of `sha256(connectionId + '\0' + modelId)` (130 bits). Deterministic on every process and in the browser.
 */
export function modelCatalogEntryId(connectionId: string, modelId: string): ModelCatalogEntryId {
  const hex = sha256Hex(`${connectionId}\0${modelId}`);
  let bits = '';
  for (let i = 0; i < hex.length; i += 2)
    bits += Number.parseInt(hex.slice(i, i + 2), 16)
      .toString(2)
      .padStart(8, '0');
  let out = '';
  for (let i = 0; i < 26 * 5; i += 5) out += BASE32[Number.parseInt(bits.slice(i, i + 5), 2)];
  return `me_${out}`;
}

/** Input/output modalities, e.g. `{ input: ['text', 'image'], output: ['text'] }`. */
export const ModelModalities = z
  .object({
    input: z.array(z.string().min(1).max(32)).max(16).default(['text']),
    output: z.array(z.string().min(1).max(32)).max(16).default(['text']),
  })
  .meta({ id: 'ModelModalities' });

/**
 * Normalized metadata of one model as a catalogue provider returns it, or as an operator enters it manually
 * (`POST /catalog/manual`, static catalogue config). Carries no approval: approval is Falak state on the entry.
 */
export const ModelCatalogEntryInput = z
  .object({
    modelId: ModelId,
    displayName: z.string().min(1).max(200).optional(),
    operation: ModelOperation,
    modalities: ModelModalities.prefault({}),
    capabilities: ModelCapabilities.prefault({}),
    limits: ModelLimits.default({}),
    price: ModelPrice.nullable().default(null),
    identity: ModelIdentity.default('exact'),
    /** For `identity: alias`: the operator-declared bounded set of underlying exact model ids. */
    aliasTargets: z.array(ModelId).max(16).optional(),
    /** Provider metadata kept verbatim for display (bounded); never interpreted as enforcement. */
    providerMetadata: JsonObject.default({}),
  })
  .meta({ id: 'ModelCatalogEntryInput' });
export type ModelCatalogEntryInput = z.infer<typeof ModelCatalogEntryInput>;
export type ModelCatalogEntryInputRaw = z.input<typeof ModelCatalogEntryInput>;

/** Falak approval record of an entry. `revision` increments on each approve/revoke (optimistic concurrency). */
export const ModelApproval = z
  .object({
    state: ApprovalState,
    revision: z.number().int().nonnegative(),
    actor: z.string().min(1).optional(),
    at: IsoDateTime.optional(),
    reason: z.string().max(1000).optional(),
    /** The approver acknowledged operator-asserted (non-provider) capabilities. */
    acknowledgedAssertions: z.boolean().default(false),
  })
  .meta({ id: 'ModelApproval' });
export type ModelApproval = z.infer<typeof ModelApproval>;

/**
 * A catalogue entry keyed by `(connectionId, modelId)`. `capabilities`/`limits`/`price` are the effective values:
 * `manualOverrides` (operator assertions) over the last provider snapshot. A refresh never changes `approval`,
 * `manualOverrides` or profile versions.
 */
export const ModelCatalogEntry = ModelCatalogEntryInput.extend({
  id: ModelCatalogEntryId,
  connectionId: ModelConnectionId,
  displayName: z.string().min(1).max(200),
  source: ModelEntrySource,
  availability: ModelAvailability,
  /** Source of `capabilities` (the provider snapshot, an operator assertion, the bootstrap import). */
  capabilitySource: CapabilitySource,
  /** Operator-asserted values that override provider metadata (shown as asserted in the UI). */
  manualOverrides: z
    .object({ capabilities: ModelCapabilityAssertions.optional(), limits: ModelLimits.optional() })
    .default({}),
  approval: ModelApproval,
  /** Time of the provider snapshot this entry was last seen in; null for manual/legacy entries. */
  fetchedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
}).meta({ id: 'ModelCatalogEntry' });
export type ModelCatalogEntry = z.infer<typeof ModelCatalogEntry>;

/**
 * Static capability declarations of a model adapter for one connection (`ModelConnectionFactory.describeCapabilities`).
 * `restrictionEnforcement` lists the restriction kinds the adapter enforces on the wire; a profile requiring any
 * other kind is rejected at activation.
 */
export const AdapterCapabilities = z
  .object({
    streaming: z.boolean(),
    structuredOutput: z.enum(['jsonSchema', 'jsonTool', 'none']),
    reasoningEffort: z.boolean(),
    temperature: z.boolean(),
    embeddings: z.boolean(),
    /** The adapter reports the provider's served model id (`ModelResponse.reportedModel`). */
    exactModelReporting: z.boolean(),
    restrictionEnforcement: z.array(z.string().min(1)).default([]),
  })
  .meta({ id: 'AdapterCapabilities' });
export type AdapterCapabilities = z.infer<typeof AdapterCapabilities>;

/** Config keys whose value would be a credential; a connection carries references (`*Ref`) only. */
const SECRET_VALUE_KEY =
  /^(api[-_]?key|apikey|token|access[-_]?token|password|secret|client[-_]?secret|authorization)$/i;
const SECRET_HEADER = /^(authorization|proxy-authorization|x-api-key|api-key|cookie)$/i;

/**
 * A named model connection (deployment YAML `models.connections[]`; added by 23). `config` is the adapter's own
 * config (e.g. `baseURL`, `apiKeyRef`, `headers`) with **secret references only**: keys ending in `Ref` (string) or
 * `Refs` (string array) name secrets resolved through `SecretsProvider` at use. Literal credential keys (`apiKey`,
 * `token`, `password` …) and credential headers (`authorization`, `x-api-key` …) are rejected. `catalogProvider`
 * names a catalogue adapter (e.g. `model-catalog-openrouter`, `model-catalog-static`) and `catalogConfig` is its
 * per-connection config (static entries live there). The UI never edits a connection.
 */
export const ModelConnection = z
  .object({
    id: ModelConnectionId,
    adapter: z.string().trim().min(1).max(100),
    enabled: z.boolean().default(true),
    displayName: z.string().min(1).max(200).optional(),
    config: JsonObject.default({}),
    catalogProvider: z.string().trim().min(1).max(100).optional(),
    catalogConfig: JsonObject.default({}),
  })
  .superRefine((connection, ctx) => {
    for (const [key, value] of Object.entries(connection.config)) {
      if (SECRET_VALUE_KEY.test(key))
        ctx.addIssue({
          code: 'custom',
          path: ['config', key],
          message: `connection config holds secret references only; use ${key}Ref with the secret name`,
        });
      if (key.endsWith('Ref') && typeof value !== 'string')
        ctx.addIssue({
          code: 'custom',
          path: ['config', key],
          message: 'a secret reference is a secret name',
        });
      if (key.endsWith('Refs') && !(Array.isArray(value) && value.every((v) => typeof v === 'string')))
        ctx.addIssue({
          code: 'custom',
          path: ['config', key],
          message: 'secret references are secret names',
        });
    }
    const headers = connection.config.headers;
    if (headers !== undefined && typeof headers === 'object' && headers !== null && !Array.isArray(headers)) {
      for (const name of Object.keys(headers))
        if (SECRET_HEADER.test(name))
          ctx.addIssue({
            code: 'custom',
            path: ['config', 'headers', name],
            message: 'credential headers come from secret references, not literal header values',
          });
    }
  })
  .meta({ id: 'ModelConnection' });
export type ModelConnection = z.infer<typeof ModelConnection>;
export type ModelConnectionInput = z.input<typeof ModelConnection>;

/**
 * Readiness of a connection, from configuration and secret **presence** only (never a provider probe).
 * `missingSecretRefs` names missing references (names, never values) and is only shown to management operators.
 */
export const ModelConnectionReadiness = z
  .object({
    connectionId: ModelConnectionId,
    state: z.enum(['ready', 'disabled', 'adapter_missing', 'secret_missing']),
    adapterRegistered: z.boolean(),
    missingSecretRefs: z.array(z.string()).default([]),
    checkedAt: IsoDateTime,
  })
  .meta({ id: 'ModelConnectionReadiness' });
export type ModelConnectionReadiness = z.infer<typeof ModelConnectionReadiness>;

/**
 * Redacted connection view for `GET /connections` and the status page: no secret values, no header values, no
 * query strings or userinfo in `endpoint`. `legacy` = synthesized `legacy-<adapter>` connection.
 */
export const ModelConnectionSummary = z
  .object({
    id: ModelConnectionId,
    adapter: z.string(),
    displayName: z.string(),
    enabled: z.boolean(),
    legacy: z.boolean(),
    configRevision: z.string().regex(/^[a-f0-9]{16}$/),
    /** `scheme://host[:port]/path` of the configured base URL, when the adapter has one. */
    endpoint: z.string().optional(),
    /** Configured header names (values never shown). */
    headerNames: z.array(z.string()).default([]),
    catalogProvider: z.string().optional(),
    capabilities: AdapterCapabilities.optional(),
    readiness: ModelConnectionReadiness,
    lastRefresh: z
      .object({
        status: z.enum(['never', 'ok', 'failed']),
        at: IsoDateTime.optional(),
        /** Last successful complete snapshot. */
        lastSuccessAt: IsoDateTime.optional(),
        entryCount: z.number().int().nonnegative().optional(),
        /** Sanitized error of the last failed refresh (no URLs with credentials, no bodies). */
        error: z.string().max(500).optional(),
      })
      .default({ status: 'never' }),
  })
  .meta({ id: 'ModelConnectionSummary' });
export type ModelConnectionSummary = z.infer<typeof ModelConnectionSummary>;

/** A restriction a profile version declares (e.g. `data_collection: deny`, `provider_allowlist: [a, b]`). */
export const ModelRestriction = z
  .object({
    kind: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/, 'restriction kind is snake_case'),
    value: z.union([z.string().max(200), z.array(z.string().max(200)).max(32)]),
    /** A required restriction the adapter cannot enforce rejects activation. */
    required: z.boolean().default(true),
  })
  .meta({ id: 'ModelRestriction' });
export type ModelRestriction = z.infer<typeof ModelRestriction>;

/** Exact profile version reference `name@version`, e.g. `reasoning@3`. */
export const ModelProfileVersionRef = z
  .string()
  .max(140)
  .regex(/^[^@\s]{1,128}@[1-9][0-9]{0,8}$/, 'fallback reference is name@version')
  .meta({ id: 'ModelProfileVersionRef' });
export type ModelProfileVersionRef = z.infer<typeof ModelProfileVersionRef>;

/** Splits `name@version`. Throws `validation_failed` on a malformed reference. */
export function parseModelProfileVersionRef(ref: string): { name: string; version: number } {
  const parsed = ModelProfileVersionRef.safeParse(ref);
  if (!parsed.success)
    throw new PlatformError('validation_failed', `Invalid profile version reference ${JSON.stringify(ref)}`);
  const at = ref.lastIndexOf('@');
  return { name: ref.slice(0, at), version: Number.parseInt(ref.slice(at + 1), 10) };
}

/**
 * One concrete, fully resolved model route. Snapshotted into a profile version (`routeSnapshot`) and into each
 * episode binding (`ResolvedRoutePlan`). Contains no secret and no endpoint; the connection is pinned by
 * `(connectionId, configRevision)`.
 */
export const ResolvedModelRoute = z
  .object({
    profileName: z.string().min(1),
    profileVersion: z.number().int().positive(),
    operation: ModelOperation,
    entryId: ModelCatalogEntryId,
    connectionId: ModelConnectionId,
    configRevision: z.string().regex(/^[a-f0-9]{16}$/),
    adapter: z.string().min(1),
    modelId: ModelId,
    settings: ModelSettings.default({}),
    capabilities: ModelCapabilities.prefault({}),
    restrictions: z.array(ModelRestriction).default([]),
    limits: ModelLimits.default({}),
    /** Price at snapshot time; null = unknown (never zero). */
    priceSnapshot: ModelPrice.nullable(),
    /** `ModelApproval.revision` of the entry when the route was resolved. */
    approvalRevision: z.number().int().nonnegative(),
    consequential: z.boolean().default(false),
  })
  .meta({ id: 'ResolvedModelRoute' });
export type ResolvedModelRoute = z.infer<typeof ResolvedModelRoute>;
export type ResolvedModelRouteInput = z.input<typeof ResolvedModelRoute>;

/** Capability evidence recorded with a version: provider-reported facts and operator assertions, kept apart. */
export const ModelCapabilityEvidence = z
  .object({
    provider: ModelCapabilityAssertions.default({}),
    operator: ModelCapabilityAssertions.default({}),
    /** Who acknowledged the operator assertions, when any are present. */
    acknowledgedBy: z.string().optional(),
  })
  .meta({ id: 'ModelCapabilityEvidence' });
export type ModelCapabilityEvidence = z.infer<typeof ModelCapabilityEvidence>;

/** Evaluation evidence of a version. Default label: `not evaluated on live provider`. */
export const ModelEvaluationEvidence = z
  .object({
    status: z.enum(['not_evaluated', 'fixture', 'live']).default('not_evaluated'),
    ref: z.string().max(500).optional(),
    label: z.string().max(200).default('not evaluated on live provider'),
  })
  .meta({ id: 'ModelEvaluationEvidence' });
export type ModelEvaluationEvidence = z.infer<typeof ModelEvaluationEvidence>;

/**
 * Immutable profile version. `hash` = `modelProfileVersionHash` (sha256 of canonical JSON without `version`,
 * `hash`, `createdAt`, `createdBy`). `fallback` holds ≤3 exact `name@version` refs (no self, no duplicates;
 * transitive cycles and same-operation are checked at activation); `routeSnapshot` holds the concrete primary and
 * fallback routes computed when the version is created and re-validated at activation.
 */
export const ModelProfileVersion = z
  .object({
    name: z.string().min(1).max(128),
    version: z.number().int().positive(),
    hash: Sha256Hex,
    description: z.string().max(2000).optional(),
    owner: z.string().max(200).optional(),
    createdBy: z.string().min(1),
    createdAt: IsoDateTime,
    source: z.enum(['bootstrap', 'managed']),
    operation: ModelOperation,
    primary: z.object({
      entryId: ModelCatalogEntryId,
      connectionId: ModelConnectionId,
      configRevision: z.string().regex(/^[a-f0-9]{16}$/),
      modelId: ModelId,
    }),
    intendedTasks: z.array(z.string()).default([]),
    consequential: z.boolean().default(false),
    region: z.string().default('global'),
    dataPolicy: z.string().default('no-training'),
    latencyClass: z.enum(['interactive', 'standard', 'batch']).default('standard'),
    maxContextTokens: z.number().int().positive().default(200_000),
    embeddingDimensions: z.number().int().positive().optional(),
    settings: ModelSettings.default({}),
    requiredCapabilities: z.array(ModelCapabilityName).default([]),
    capabilityEvidence: ModelCapabilityEvidence.prefault({}),
    restrictions: z.array(ModelRestriction).default([]),
    fallback: z.array(ModelProfileVersionRef).max(3).default([]),
    routeSnapshot: z.object({
      primary: ResolvedModelRoute,
      fallbacks: z.array(ResolvedModelRoute).max(3).default([]),
    }),
    evaluation: ModelEvaluationEvidence.prefault({}),
    activationRationale: z.string().max(2000).optional(),
    /** The creator acknowledged that the primary entry is `unlisted`/`unknown` in the catalogue. */
    acknowledgeUnlisted: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    const seen = new Set<string>();
    for (const [i, ref] of v.fallback.entries()) {
      const name = ref.slice(0, ref.lastIndexOf('@'));
      if (name === v.name)
        ctx.addIssue({
          code: 'custom',
          path: ['fallback', i],
          message: 'a profile cannot fall back to itself',
        });
      if (seen.has(name))
        ctx.addIssue({ code: 'custom', path: ['fallback', i], message: 'duplicate fallback profile' });
      seen.add(name);
    }
    if (v.routeSnapshot.fallbacks.length !== v.fallback.length)
      ctx.addIssue({
        code: 'custom',
        path: ['routeSnapshot', 'fallbacks'],
        message: 'one route per fallback reference',
      });
  })
  .meta({ id: 'ModelProfileVersion' });
export type ModelProfileVersion = z.infer<typeof ModelProfileVersion>;
export type ModelProfileVersionInput = z.input<typeof ModelProfileVersion>;

/** Content hash of a profile version: sha256 of canonical JSON without `version`, `hash`, `createdAt`, `createdBy`. */
export function modelProfileVersionHash(
  version: Omit<ModelProfileVersion, 'hash' | 'version' | 'createdAt' | 'createdBy'> &
    Partial<Pick<ModelProfileVersion, 'hash' | 'version' | 'createdAt' | 'createdBy'>>,
): string {
  const { hash: _h, version: _v, createdAt: _c, createdBy: _b, ...content } = version;
  return sha256Hex(canonicalJson(content));
}

/** Mutable pointer of a profile name: its active version and enabled state. `revision` guards every write. */
export const ModelProfilePointer = z
  .object({
    name: z.string().min(1).max(128),
    /** null = no active version (never activated, or deactivated). */
    activeVersion: z.number().int().positive().nullable(),
    enabled: z.boolean(),
    revision: z.number().int().nonnegative(),
    updatedAt: IsoDateTime,
    updatedBy: z.string().min(1),
  })
  .meta({ id: 'ModelProfilePointer' });
export type ModelProfilePointer = z.infer<typeof ModelProfilePointer>;

/** The deployment-wide default profile name (single row). `revision` guards `PATCH /default`. */
export const ModelDefaultPointer = z
  .object({
    profile: z.string().min(1).max(128),
    revision: z.number().int().nonnegative(),
    updatedAt: IsoDateTime,
    updatedBy: z.string().min(1),
  })
  .meta({ id: 'ModelDefaultPointer' });
export type ModelDefaultPointer = z.infer<typeof ModelDefaultPointer>;

/**
 * Route plan resolved once per episode attempt (or once per non-episode operation). `requestedProfile` is the agent's
 * `spec.modelProfile`; `profileName` is the resolved name (differs only when `followsDefault`, ADR-006/ADR-022).
 * `planHash` = sha256 of canonical JSON of the plan without `planHash` and `resolvedAt`.
 */
export const ResolvedRoutePlan = z
  .object({
    requestedProfile: z.string().min(1),
    profileName: z.string().min(1),
    profileVersion: z.number().int().positive(),
    profileHash: Sha256Hex,
    operation: ModelOperation,
    consequential: z.boolean(),
    /** The requested name is the deployment's default alias and was resolved through the default pointer. */
    followsDefault: z.boolean(),
    primary: ResolvedModelRoute,
    /** Ordered fallback routes; empty for consequential profiles. */
    fallbacks: z.array(ResolvedModelRoute).max(3).default([]),
    /** `managed` = from registry DB state; `static` = from YAML profiles (management disabled / memory). */
    source: z.enum(['managed', 'static']),
    resolvedAt: IsoDateTime,
    planHash: Sha256Hex,
  })
  .meta({ id: 'ResolvedRoutePlan' });
export type ResolvedRoutePlan = z.infer<typeof ResolvedRoutePlan>;
export type ResolvedRoutePlanInput = z.input<typeof ResolvedRoutePlan>;

/** Hash of a route plan (excludes `planHash` and `resolvedAt`, so re-resolving the same state gives the same hash). */
export function resolvedRoutePlanHash(
  plan: Omit<ResolvedRoutePlan, 'planHash' | 'resolvedAt'> &
    Partial<Pick<ResolvedRoutePlan, 'planHash' | 'resolvedAt'>>,
): string {
  const { planHash: _p, resolvedAt: _r, ...content } = plan;
  return sha256Hex(canonicalJson(content));
}

/**
 * Immutable per-attempt binding (`core_agent_runtime.model_bindings`, PK `(tenant_id, task_id, step_id, attempt)`).
 * `hash` = `plan.planHash`. Requests carry `{ bindingId, hash, routeIndex }` (`ModelRequest.binding`).
 */
export const ModelBinding = z
  .object({
    bindingId: z.string().min(1).max(128),
    tenantId: TenantId,
    taskId: z.string().min(1),
    stepId: z.string().min(1),
    attempt: z.number().int().nonnegative(),
    plan: ResolvedRoutePlan,
    hash: Sha256Hex,
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ModelBinding' });
export type ModelBinding = z.infer<typeof ModelBinding>;

/** Cost coverage of a call or aggregate: `known` (all components priced), `partial`, `unknown` (never shown as 0). */
export const ModelCostStatus = z.enum(['known', 'partial', 'unknown']).meta({ id: 'ModelCostStatus' });
export type ModelCostStatus = z.infer<typeof ModelCostStatus>;

/** Gateway method of a call record. One `embed` call (any number of inputs) is one record. */
export const ModelCallMethod = z
  .enum(['generateText', 'generateObject', 'streamText', 'embed'])
  .meta({ id: 'ModelCallMethod' });
export type ModelCallMethod = z.infer<typeof ModelCallMethod>;

/**
 * Durable record of one gateway operation (`core_agent_runtime.model_call_records`): `started` before dispatch,
 * finalized after; a crash leaves `outcome_unknown`. No prompt, completion or secret is stored. `costUsd` is a decimal
 * string or null (unknown); price source order: binding snapshot → configured price → unknown.
 */
export const ModelCallRecord = z
  .object({
    callId: z.string().min(1).max(128),
    tenantId: TenantId,
    taskId: z.string().optional(),
    stepId: z.string().optional(),
    attempt: z.number().int().nonnegative().optional(),
    bindingId: z.string().optional(),
    bindingHash: Sha256Hex.optional(),
    /** 0 = primary, n = nth fallback route of the binding. */
    routeIndex: z.number().int().nonnegative().default(0),
    profileName: z.string().min(1),
    profileVersion: z.number().int().positive().optional(),
    entryId: ModelCatalogEntryId.optional(),
    connectionId: ModelConnectionId,
    configRevision: z.string().regex(/^[a-f0-9]{16}$/),
    method: ModelCallMethod,
    requestedModel: ModelId,
    /** Provider-reported served model (absent when the adapter/provider does not report it). */
    reportedModel: z.string().max(256).optional(),
    reportedProvider: z.string().max(128).optional(),
    /** `reportedModel` present and different from `requestedModel`. */
    modelMismatch: z.boolean().default(false),
    status: z.enum(['started', 'succeeded', 'failed', 'cancelled', 'outcome_unknown']),
    errorCode: ErrorCode.optional(),
    /** The failed call this one falls back from, and why. */
    fallbackOf: z.string().optional(),
    fallbackReason: z.string().max(200).optional(),
    startedAt: IsoDateTime,
    finishedAt: IsoDateTime.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    usage: z
      .object({
        inputTokens: z.number().int().nonnegative().optional(),
        outputTokens: z.number().int().nonnegative().optional(),
        reasoningTokens: z.number().int().nonnegative().optional(),
      })
      .nullable(),
    priceSource: z.enum(['binding', 'configured', 'unknown']),
    priceAsOf: IsoDateTime.optional(),
    costStatus: ModelCostStatus,
    costUsd: DecimalString.nullable(),
  })
  .meta({ id: 'ModelCallRecord' });
export type ModelCallRecord = z.infer<typeof ModelCallRecord>;

/** Counts of call records by cost status. */
export const ModelCostCoverage = z
  .object({
    known: z.number().int().nonnegative(),
    partial: z.number().int().nonnegative(),
    unknown: z.number().int().nonnegative(),
  })
  .meta({ id: 'ModelCostCoverage' });
export type ModelCostCoverage = z.infer<typeof ModelCostCoverage>;

/**
 * Cost aggregate per requested model (and reported model where known). `knownCostUsd` sums the known/partial parts
 * only and is null when no call of the group has a known price (never `0` for unknown); `costStatus` is `known` only
 * when every call is known, `unknown` when none is.
 */
export const ModelCostAggregate = z
  .object({
    requestedModel: z.string(),
    reportedModel: z.string().optional(),
    profileName: z.string().optional(),
    connectionId: z.string().optional(),
    calls: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    knownCostUsd: DecimalString.nullable(),
    costStatus: ModelCostStatus,
    coverage: ModelCostCoverage,
  })
  .meta({ id: 'ModelCostAggregate' });
export type ModelCostAggregate = z.infer<typeof ModelCostAggregate>;

/** Management operations recorded in the audit. */
export const ModelManagementOperation = z
  .enum([
    'bootstrap',
    'catalog.refresh',
    'catalog.manual',
    'catalog.approve',
    'catalog.revoke',
    'profile.version.create',
    'profile.activate',
    'profile.deactivate',
    'default.set',
  ])
  .meta({ id: 'ModelManagementOperation' });
export type ModelManagementOperation = z.infer<typeof ModelManagementOperation>;

/**
 * One management audit record, written in the mutation's transaction. Hashes are of redacted state; no secrets,
 * headers, prompts or completions.
 */
export const ModelManagementAudit = z
  .object({
    id: z.string().min(1),
    at: IsoDateTime,
    actor: z.string().min(1),
    actorTenantId: TenantId,
    managementTenantId: TenantId.optional(),
    operation: ModelManagementOperation,
    target: z.object({
      kind: z.enum(['entry', 'profile', 'default', 'connection', 'deployment']),
      name: z.string().optional(),
      entryId: ModelCatalogEntryId.optional(),
      connectionId: ModelConnectionId.optional(),
      version: z.number().int().positive().optional(),
    }),
    reason: z.string().max(1000).optional(),
    beforeRevision: z.number().int().nonnegative().optional(),
    afterRevision: z.number().int().nonnegative().optional(),
    beforeHash: Sha256Hex.optional(),
    afterHash: Sha256Hex.optional(),
    details: JsonObject.default({}),
  })
  .meta({ id: 'ModelManagementAudit' });
export type ModelManagementAudit = z.infer<typeof ModelManagementAudit>;

/** One drift item: YAML differs from bootstrap version 1 (recorded as a warning, never auto-imported). */
export const ModelDriftItem = z
  .object({
    profile: z.string(),
    field: z.string(),
    yaml: z.unknown(),
    managed: z.unknown(),
  })
  .meta({ id: 'ModelDriftItem' });
export type ModelDriftItem = z.infer<typeof ModelDriftItem>;

/** `GET /registry/model-management/status`: enabled state, caller capabilities, scope, migration status. */
export const ModelManagementStatus = z
  .object({
    enabled: z.boolean(),
    /** Shown only to callers inside the management tenant. */
    managementTenantId: TenantId.optional(),
    caller: z.object({
      canRead: z.boolean(),
      canManage: z.boolean(),
      /** Why `canManage` is false. */
      reason: z.enum(['management_disabled', 'role', 'tenant']).optional(),
    }),
    bootstrap: z.object({
      state: z.enum(['pending', 'done']),
      bootstrappedAt: IsoDateTime.optional(),
      drift: z.array(ModelDriftItem).default([]),
    }),
    connections: z.number().int().nonnegative(),
    profiles: z.number().int().nonnegative(),
    defaultProfile: z.string(),
  })
  .meta({ id: 'ModelManagementStatus' });
export type ModelManagementStatus = z.infer<typeof ModelManagementStatus>;

/**
 * Effective configuration of one profile name: the single source for `/registry/models` (`effective`), admin stats,
 * Studio and Settings (decision B.5). Never reconstructed from boot YAML by a UI.
 */
export const EffectiveModelProjection = z
  .object({
    name: z.string(),
    operation: ModelOperation,
    activeVersion: z.number().int().positive().nullable(),
    profileHash: Sha256Hex.optional(),
    /** Concrete model id sent to the provider (after legacy override normalization). */
    modelId: z.string(),
    adapter: z.string(),
    entryId: ModelCatalogEntryId.optional(),
    connectionId: ModelConnectionId,
    configRevision: z.string().regex(/^[a-f0-9]{16}$/),
    approvalState: ApprovalState,
    availability: ModelAvailability.default('unknown'),
    enabled: z.boolean(),
    isDefault: z.boolean(),
    consequential: z.boolean(),
    intendedTasks: z.array(z.string()).default([]),
    /** Fallback refs `name@version` of the active version, in order. */
    fallbackNames: z.array(z.string()).default([]),
    impactedAgentCount: z.number().int().nonnegative(),
    maxContextTokens: z.number().int().positive().optional(),
    embeddingDimensions: z.number().int().positive().optional(),
    source: z.enum(['managed', 'static']),
  })
  .meta({ id: 'EffectiveModelProjection' });
export type EffectiveModelProjection = z.infer<typeof EffectiveModelProjection>;

/**
 * `registry.model-management.changed` (added by 23): emitted after a management mutation commits. Cache
 * invalidation only; correctness never depends on its delivery. `revision` is the changed record's new revision.
 */
export const RegistryModelManagementChangedData = z
  .object({
    kind: z.enum(['catalog', 'approval', 'profile', 'default', 'connection', 'bootstrap']),
    name: z.string().optional(),
    entryId: ModelCatalogEntryId.optional(),
    revision: z.number().int().nonnegative(),
  })
  .meta({ id: 'RegistryModelManagementChangedData' });
export type RegistryModelManagementChangedData = z.infer<typeof RegistryModelManagementChangedData>;
